import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { publicationAssets } from '@/constants/publicationAssets';
import useIsMobile from '@/hooks/useIsMobile';
import { downloadFileFromFrontend } from '@/utils/fileUtils.ts';

import * as S from './style.ts';

type ReceiptRow = {
	issue: string;
	publishDate: string;
};

type ManuscriptGuideLine = {
	className?: string;
	text: string;
};

type ProcessCard = {
	action: 'signup' | 'manuscriptTemplate' | 'ethicsPledge' | 'copyrightConsent';
	button: string;
	label: string;
};

const SUBMISSION_SYSTEM_URL = 'https://kyobo025.medone.co.kr/html/';

const PublicationSubmissionGuide: React.FC = () => {
	const { t } = useTranslation();
	const isMobile = useIsMobile(900);
	const navigate = useNavigate();
	const receiptRows = t('publication.submissionGuide.receiptRows', { returnObjects: true }) as ReceiptRow[];
	const manuscriptGuideLines = t('publication.submissionGuide.manuscriptGuideLines', { returnObjects: true }) as ManuscriptGuideLine[];
	const processCards = t('publication.submissionGuide.processCards', { returnObjects: true }) as ProcessCard[];
	const guideItemKeys = [
		'publication.submissionGuide.guide.item1',
		'publication.submissionGuide.guide.item2',
		'publication.submissionGuide.guide.item3',
		'publication.submissionGuide.guide.item4',
	];

	const handleAction = (action: ProcessCard['action']) => {
		switch (action) {
			case 'signup':
				navigate('/auth/signup');
				break;
			case 'manuscriptTemplate':
				downloadFileFromFrontend(publicationAssets.manuscriptTemplateHwp, t('publication.submissionGuide.downloadNames.manuscriptTemplate'));
				break;
			case 'ethicsPledge':
				downloadFileFromFrontend(publicationAssets.ethicsPledgeFormHwp, t('publication.submissionGuide.downloadNames.ethicsPledge'));
				break;
			case 'copyrightConsent':
				downloadFileFromFrontend(publicationAssets.copyrightConsentFormHwp, t('publication.submissionGuide.downloadNames.copyrightConsent'));
				break;
		}
	};

	const renderCardLabel = (label: string) => {
		const segments = label.split('\n');

		if (isMobile) {
			return <p>{segments.join(' ')}</p>;
		}

		return (
			<p>
				{segments.map((segment, index) => (
					<React.Fragment key={`${label}-${index}`}>
						{segment}
						{index < segments.length - 1 ? <br /> : null}
					</React.Fragment>
				))}
			</p>
		);
	};

	return (
		<S.Container>
			<S.ContentBox>
				<S.ContentTitle>
					{t('publication.submissionGuide.title')}
					<a href={SUBMISSION_SYSTEM_URL} target="_blank" rel="noopener noreferrer">
						{t('publication.submissionGuide.jamsLinkLabel')}
					</a>
				</S.ContentTitle>
				<S.ContentItems>
					{guideItemKeys.map((key, index) => (
						<S.ContentListLayout key={key}>
							<S.ContentTitleSub>{String(index + 1).padStart(2, '0')}</S.ContentTitleSub>
							<S.ContentDescription>{t(key)}</S.ContentDescription>
						</S.ContentListLayout>
					))}
				</S.ContentItems>
			</S.ContentBox>

			<S.ContentBox>
				<S.ContentTitle>{t('publication.submissionGuide.manuscriptGuideTitle')}</S.ContentTitle>
				<S.ManuscriptGuide>
					{manuscriptGuideLines.map((line, index) => (
						<S.ManuscriptGuideLine key={`${line.className ?? 'line'}-${index}`} className={line.className}>
							{line.text}
						</S.ManuscriptGuideLine>
					))}
				</S.ManuscriptGuide>
			</S.ContentBox>

			<S.ContentBox>
				<S.ContentTitle>{t('publication.submissionGuide.referenceGuideTitle')}</S.ContentTitle>
				<S.ContentDescription>{t('publication.submissionGuide.referenceGuideBody')}</S.ContentDescription>
			</S.ContentBox>

			<S.ContentBox>
				<S.ContentTitle>{t('publication.submissionGuide.receiptTitle')}</S.ContentTitle>
				<S.TablePanel>
				<S.Table className="table1">
					<S.TableHeadCell className="item1 center">{t('publication.submissionGuide.receiptHeaders.issue')}</S.TableHeadCell>
					<S.TableHeadCell className="center">{t('publication.submissionGuide.receiptHeaders.publishDate')}</S.TableHeadCell>

					<S.TableCell className="min-height point item2 center" style={{ gridRowEnd: receiptRows.length + 2 }}>
						{t('publication.submissionGuide.receiptYearlyLabel')}
					</S.TableCell>

					{receiptRows.map((row) => (
						<React.Fragment key={row.issue}>
							<S.TableCell className="min-height point center">{row.issue}</S.TableCell>
							<S.TableCell className="min-height center">{row.publishDate}</S.TableCell>
						</React.Fragment>
					))}
				</S.Table>
				</S.TablePanel>
				<S.ContentDescription>{t('publication.submissionGuide.receiptNote')}</S.ContentDescription>
			</S.ContentBox>

			<S.ContentBox>
				<S.ContentTitle>{t('publication.submissionGuide.processTitle')}</S.ContentTitle>
				<S.TablePanel>
				<S.Table className="table2">
					{processCards.map((card, index) => (
						<S.TableHeadCell key={`${card.action}-head`} style={{ gridArea: `item${index + 1}` }} className="center">
							{String(index + 1).padStart(2, '0')}
						</S.TableHeadCell>
					))}

					{processCards.map((card, index) => (
						<S.TableCell key={card.action} style={{ gridArea: `item${index + 5}` }} className="min-height point center">
							<S.TableCellLayout>
								{renderCardLabel(card.label)}
								<S.Button onClick={() => handleAction(card.action)}>{card.button}</S.Button>
							</S.TableCellLayout>
						</S.TableCell>
					))}
				</S.Table>
				</S.TablePanel>
			</S.ContentBox>
		</S.Container>
	);
};

export default PublicationSubmissionGuide;
